import { useCallback, useEffect, useMemo, useState } from 'react'
import { get as getLocalStorage, set as setLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import {
  CHAT_FEEDBACK_POPUP_STORAGE_KEY,
  getUserMessageCount,
  shouldShowChatFeedbackPopup,
  trackChatFeedbackPopupAppeared,
  trackChatFeedbackSubmitted,
} from '../utils/feedbackPopup'

function getLastAppearedAt(): number | undefined {
  const value = getLocalStorage(CHAT_FEEDBACK_POPUP_STORAGE_KEY)
  return typeof value === 'number' ? value : undefined
}

interface UseChatFeedbackPopupProps {
  chatId: number | undefined
  messages: Array<MessageItem>
}

export function useChatFeedbackPopup({ chatId, messages }: UseChatFeedbackPopupProps) {
  const [visibleChatId, setVisibleChatId] = useState<number | undefined>(undefined)

  const userMessageCount = useMemo(() => getUserMessageCount(messages), [messages])

  useEffect(() => {
    if (chatId === undefined) {
      setVisibleChatId(undefined)
      return
    }

    if (visibleChatId === chatId) {
      return
    }

    const now = Date.now()
    const lastAppearedAt = getLastAppearedAt()

    if (!shouldShowChatFeedbackPopup(chatId, userMessageCount, lastAppearedAt, now)) {
      setVisibleChatId(undefined)
      return
    }

    setLocalStorage(CHAT_FEEDBACK_POPUP_STORAGE_KEY, now)
    setVisibleChatId(chatId)
    trackChatFeedbackPopupAppeared(chatId, userMessageCount)
  }, [chatId, userMessageCount, visibleChatId])

  const dismiss = useCallback(() => {
    setVisibleChatId(undefined)
  }, [])

  const submit = useCallback((score: number | undefined, comment: string) => {
    if (visibleChatId === undefined) {
      return
    }

    trackChatFeedbackSubmitted(visibleChatId, score, comment)
    dismiss()
  }, [dismiss, visibleChatId])

  return {
    isVisible: visibleChatId !== undefined,
    dismiss,
    submit,
  }
}
